import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {classMap} from 'lit/directives/class-map.js';
import store from '../store';
import {getProductData} from '../util/getProductData';
import {featureInSection} from '../util/featureInSection';
import {regionCheck} from '../util/regionCheck';
import {
	selectCurrentSection,
	selectLuminary,
	selectProductData,
	selectSectionConfig,
	selectSpcAttributes, selectUnFilteredSpcAttributes,
} from '../selectors';
import {selectSolarLumPosition2IsVisible} from '../solar/solarSelectors';
import {stringToAttributeId} from './spc/util/spcUtils';
import {luminaryOptions} from '../lightProfile/lightProfileSectionConfig';

@customElement('feature-container')
export class FeatureContainer extends connect(store)(LitElement) {

	static styles = css`
		:host {
			height: 100%;
			width: 100%;
		}

		.container {
			width: var(--features-column-width);
			overflow-y: scroll;
			padding: 0 calc(100vw - var(--features-column-width) - var(--large-padding)) 0 var(--large-padding);
			pointer-events: none;
			height: 100%;
			overflow-x: hidden;
		}

		.container.columns {
			//columns: var(--features-column-width) 6;
			//column-fill: balance;
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
			grid-template-rows: 1fr;
			grid-column-gap: 32px;
			grid-row-gap: 0;
			width: 100%;
			box-sizing: border-box;
			padding: 0 var(--large-padding) 0 var(--large-padding);
		}

		// for Opera, Chrome, Edge, and Safari, Firefox

		:host::-webkit-scrollbar {
			display: none;
		}

		@media (max-width: 768px) {
			:host {
				align-self: flex-start;
				padding-top: var(--large-padding);
				padding-left: var(--small-padding);
				padding-right: var(--small-padding);
			}

			.container {
				width: 100%;
				padding-left: 0;
				padding-right: 0;
			}
		}

		.features {
			break-inside: avoid;
			padding-bottom: var(--large-space);
			width: 100%;
			pointer-events: none;
			display: none;
			flex-flow: column;
			place-content: flex-start;
			gap: var(--large-space);
			margin-bottom: 90px;
		}
		.breakInside {
			break-inside: auto;
		}
		.features:has(> *) {
			pointer-events: all;
		}

		.header {
			font: var(--font-widget-label);
			color: var(--color-grey4);
			padding-bottom: var(--small-padding);
			border-bottom: 1px solid var(--color-white);
			display: none;
		}

		.dashboard .header {
			display: block;
		}

		.features > * {
			pointer-events: auto;
		}

		@media (max-width: 768px) {
			:host {
				padding: 0 var(--small-padding) var(--small-padding);
			}

			.features {
				min-width: calc(100vw - var(--small-padding) * 2);
				padding-bottom: 90px;
			}
		}

		.active {
			display: flex;
			pointer-events: all
		}

		.dashboard {
			display: flex;
			pointer-events: all
		}
	`;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.translations = state.productData.translations;
		this.productData = selectProductData(this.state);
		this.sectionConfig = selectSectionConfig(state);
		this.region = state.status.region;
		this.lang = state.status.lang;
		this.currentSection = selectCurrentSection(state);
		this.dashboardActive = state.status.dashboardActive;
		this.isLightProfile = state.status.family === 'LightProfile';
		this.isSolar = state.status.family === 'Aeto';
		this.internal = state.status.mode === 'internal';

		// this.spcConfiguration = selectSpcConfiguration(state);
		this.spcAttributes = selectSpcAttributes(state) ?? [];
		this.debugShowAll = state.status.debugShowAll;
	}
	isSatisfied(feature) {
		// return (feature.variableId in this.decisions.normalizedFeatures && this.decisions.normalizedFeatures[feature.variableId].isSatisfied);
	}
	canBeOmitted(feature) {
		return false;
		// satisfied and only one value available
		/*
		return this.isSatisfied(feature) && feature.values.filter(value => {
			const key = feature.variableId + '.' + value.featureValueId;
			return (key in this.decisions.normalizedDecisions && this.decisions.normalizedDecisions[key].canBeIncluded);
		}).length <= 1;
		 */
	}
	render() {
		// if (!this.spcAttributes) return null;
		//TODO: remove hack to get the model select into the luminaire section
		return html`
			<div class="container ${classMap({columns: this.dashboardActive})}">
				${repeat(
					this.sectionConfig,
					(section) => html`
						${when((this.dashboardActive && section.notInDashboard !== true) || (!this.dashboardActive && section.id === this.currentSection), () => html`
							<div class="features ${classMap({active: !this.dashboardActive && section.id === this.currentSection, dashboard: this.dashboardActive})}">
								${when(section.label, () => html`<div class="header">${section.label[this.lang]}</div>`)}
								${(this.dashboardActive || section.id === this.currentSection) ? html`
									${section.widgets}
									${this.isLightProfile && section.id === 'luminaire' ? luminaryOptions.find(luminary => luminary.id === selectLuminary(this.state))?.widgets : null}
									${repeat(
										this.spcAttributes
											// special case for eato: remove second lum settings when not selected
											.filter(spcAttribute => !(this.isSolar && !selectSolarLumPosition2IsVisible(this.state) && spcAttribute.key === 'root::LE_LightDistribution_2'))
											.filter(spcAttribute => !(this.isSolar && !selectSolarLumPosition2IsVisible(this.state) && spcAttribute.key === 'root::LE_LuminairePositionHeightSU_2'))
											.filter(spcAttribute => this.debugShowAll || !this.isLightProfile || luminaryOptions.find(luminary => luminary.id === selectLuminary(this.state))?.features.includes(spcAttribute.key))
											.filter(spcAttribute => !section.excludeFeatures?.includes(spcAttribute.key))
											.filter(feature => featureInSection(this.sectionConfig, section, feature.key))
											.filter(spcAttribute => {
												const featureProductData = getProductData(spcAttribute.id.localId, this.productData);
												return this.debugShowAll || (featureProductData?.isVisible && regionCheck(featureProductData, this.region));
											})
											.filter(feature => !this.canBeOmitted(feature))
											.sort((a, b) => getProductData(a.id.localId, this.productData)?.displaySequenceUi - getProductData(b.id.localId, this.productData)?.displaySequenceUi),

										(spcAttribute) => spcAttribute.key,
										(spcAttribute, index) => {
											const attributeID = stringToAttributeId(spcAttribute.key);
											// 'colorSelect','compactSelect', 'comboSelect', 'reducedSelect', 'reducedChoiceSelect'
											return html`
												<spc-attribute .attributeId=${attributeID}></spc-attribute>`;
										})
									}
									${section.widgetsEnd}
								` : null}
							</div>
						`)}
					`)
				}
			</div>
		`;
	}
}
